import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { ReportHeader } from "../../components/ReportHeader";
import api from "../../../../api/landlord/applications";
import { Applicant } from "types/application";
import { Section } from "../../components/Section";
import { User03, AlertCircle } from "untitledui-js-base";
import { formatPhone } from "shared/utils";
import ChildCare from "../../../../shared/icons/childCare.svg?react";
import PetsIcon from "../../../../shared/icons/pets.svg?react";
import CarIcon from "../../../../shared/icons/car.svg?react";
import { DynamicKeyValue } from "../../components/SmartRender/DynamicKeyValue";
import AdditionalInformationIcon from "../../../../shared/icons/additionalInformation.svg?react";
import { Application } from "../../../../types/application";
import moment from "moment";
import { Row } from "../../components/Row";
import { Loader } from "shared/components/Loader";
import { useMemo } from "react";
import { ContentTitle } from "../../components/ContentTitle";
import { VerificationNames } from "shared/types";
import { getVerificationQueryKeys } from "../../utils/getVerificationQueryKeys";
import { PrintReady } from "../../providers/PrintProvider";
import { formatToCurrency } from "../../../../utils/currency";
import { PetAssetItem } from "./PetAssetItem";
import { ObjectItem } from "../../components/SmartRender/ObjectItem";
import { Show } from "shared/components";

interface BasicInformationProps {
  applicant: Applicant;
  verificationId: number;
  email?: string;
  phone?: string;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
}

interface RentalApplicationSchema {
  lead_source_step_enabled?: boolean;
  additional_questions_step_enabled?: boolean;
  military_first_responder_teacher_step_enabled?: boolean;
  personal_details_step_enabled?: boolean;
  pets_step_enabled?: boolean;
  vehicles_step_enabled?: boolean;
  dependents_step_enabled?: boolean;
  emergency_contacts_step_enabled?: boolean;
}

export const BasicInformation = ({
  applicant,
  verificationId,
  email,
  phone,
  applicationMeta
}: BasicInformationProps) => {
  const query = useQuery({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.RentalApplication,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then((data) => data.verification)
  });
  const formatDate = (date: string | Date | null | undefined) => {
    return date ? moment(date).format("MMM DD, YYYY [at] h:mm A") : "--";
  };

  if (query.isLoading)
    <>
      <ReportHeader
        title="Application summary"
        reportDate={query.data?.updated_at}
        verificationId={0}
      />
      <Loader />
    </>;

  const additionalQuestionsOrder = useMemo(() => {
    return (
      applicationMeta.data?.verification_template?.verification_schemas[
        applicant.role || ""
      ][VerificationNames.RentalApplication]?.additional_questions?.map(
        (ac) => ac?.name
      ) || []
    );
  }, [applicationMeta.data]);

  const mutatedDependents = useMemo(() => {
    return query.data?.report?.list_of_dependents?.map((el) => {
      return {
        ...el,
        age: el?.dob
          ? moment(applicant.application_finished_at).diff(el?.dob, "years")
          : "-"
      };
    });
  }, [query.data]);

  const {
    lead_source_step_enabled,
    military_first_responder_teacher_step_enabled
  } = applicationMeta.data?.verification_template?.verification_schemas
    .applicant.rental_application as RentalApplicationSchema;

  return (
    <div className="flex-1">
      <ReportHeader
        title="Application summary"
        reportDate={query.data?.updated_at}
        verificationId={verificationId}
      />

      <PrintReady verificationId={verificationId} />

      <Section sectionTitle="Basic information" icon={<User03 size="20px" />}>
        <div className="flex w-full max-w-[600px] flex-col">
          <ContentTitle title="Application info">
            <DynamicKeyValue
              detailsKey="Application started"
              value={formatDate(applicant.application_started_at)}
            />
            <DynamicKeyValue
              detailsKey="Application completed"
              value={
                query.data?.completed_at &&
                formatDate(applicant.application_finished_at)
              }
            />
            <Row
              title="Group application"
              value={
                (applicationMeta.data?.applicants.length || 0) > 1
                  ? "Yes"
                  : "No"
              }
            />
            {(applicationMeta.data?.applicants.length || 0) > 1 ? (
              <DynamicKeyValue
                detailsKey="Group application completed"
                value={
                  applicationMeta.data?.applicants.some(
                    (applicant) => applicant.application_finished_at
                  )
                    ? applicationMeta.data.applicants
                        .reduce((latest, applicant) => {
                          const current = moment(
                            applicant.application_finished_at
                          );
                          return current.isAfter(latest) ? current : latest;
                        }, moment(0))
                        .format("MMM DD, YYYY [at] h:mm A")
                    : null
                }
              />
            ) : null}
          </ContentTitle>

          <ContentTitle title="Application for">
            <DynamicKeyValue
              detailsKey="Property"
              value={applicationMeta.data?.property.name}
            />
            <DynamicKeyValue
              detailsKey="Unit"
              value={applicationMeta.data?.unit.title}
            />
            <DynamicKeyValue
              detailsKey="Address"
              value={applicationMeta.data?.property.address.one_line_address}
            />
            <Row
              className="my-2"
              title="Monthly rent"
              value={formatToCurrency(
                applicationMeta.data?.monthly_rent_amount ?? "--",
                { maximumFractionDigits: 0 }
              )}
            />
            <Row
              className="my-2"
              title="Desired move-in date"
              value={
                applicationMeta.data?.desired_move_in_date &&
                moment(applicationMeta.data.desired_move_in_date).isValid()
                  ? moment(applicationMeta.data.desired_move_in_date).format(
                      "ll"
                    )
                  : "--"
              }
            />
          </ContentTitle>

          <ContentTitle title="Applicant">
            <DynamicKeyValue
              detailsKey="First name"
              value={applicant.first_name}
            />
            <DynamicKeyValue
              detailsKey="Last name"
              value={applicant.last_name}
            />
            <DynamicKeyValue
              detailsKey="Middle name"
              value={applicant.middle_name || "--"}
            />
            <DynamicKeyValue
              detailsKey="Date of birth"
              value={
                query.data?.report?.[VerificationNames.IdentityVerification]
                  ?.dob
              }
              fullDate
            />
            <DynamicKeyValue
              detailsKey="Age"
              value={
                query.data?.report?.[VerificationNames.IdentityVerification]
                  ?.dob
                  ? moment(
                      applicant.application_finished_at || new Date() // In case the application is not finished
                    ).diff(
                      query.data?.report?.[
                        VerificationNames.IdentityVerification
                      ].dob,
                      "years"
                    )
                  : "--"
              }
            />
            <DynamicKeyValue
              detailsKey="SSN"
              value={
                query.data?.report?.[VerificationNames.IdentityVerification]
                  ?.ssn
              }
            />
            <DynamicKeyValue
              detailsKey="Marital status"
              value={query.data?.report?.marital_status}
            />

            <DynamicKeyValue
              detailsKey="Phone"
              value={formatPhone(phone || "")}
            />
            <DynamicKeyValue detailsKey="Email" value={email} />
            <DynamicKeyValue
              detailsKey="Military, first responder, or teacher"
              isUnavailableValue={
                military_first_responder_teacher_step_enabled &&
                !query.data?.report.are_you_military_first_responder_teacher
              }
              value={
                query.data?.report.are_you_military_first_responder_teacher
              }
            />
            <DynamicKeyValue
              detailsKey="Lead source"
              isUnavailableValue={
                lead_source_step_enabled && !query.data?.report.lead_source
              }
              value={query.data?.report.lead_source}
            />
          </ContentTitle>
        </div>
      </Section>

      {"dependents" in (query.data?.report || {}) && (
        <Section sectionTitle="Dependents" icon={<ChildCare />}>
          <div className="w-full max-w-[600px]">
            {query.data?.report?.list_of_dependents?.length > 0 ? (
              <DynamicKeyValue
                detailsKey="Person"
                value={mutatedDependents}
                order={["first_name", "last_name", "dob", "age"]}
              />
            ) : (
              <DynamicKeyValue detailsKey="Dependents" value="No" />
            )}
          </div>
        </Section>
      )}

      {"emergency_contacts" in (query.data?.report || {}) && (
        <Section
          sectionTitle="Emergency contacts"
          icon={<AlertCircle size="20px" />}
        >
          <div className="w-full max-w-[600px]">
            {query.data?.report?.emergency_contacts?.length > 0 ? (
              <DynamicKeyValue
                detailsKey="Contact"
                value={query.data?.report?.emergency_contacts}
                order={[
                  "first_name",
                  "last_name",
                  "relationship",
                  "phone",
                  "email"
                ]}
              />
            ) : (
              <p className="text-sm font-medium text-content-300">
                No Emergency contacts provided
              </p>
            )}
          </div>
        </Section>
      )}

      {"do_you_have_pets" in (query.data?.report || {}) && (
        <Section sectionTitle="Pets and Service Animals" icon={<PetsIcon />}>
          <ContentTitle title="Summary">
            <Row
              title="Pets and service animals"
              value={query.data?.report?.do_you_have_pets}
            />
          </ContentTitle>
          <div className="w-full max-w-[600px]">
            {query.data.report.pets?.map((pet, index) => (
              <ContentTitle key={pet.id} title={`Animal ${index + 1}`}>
                <div className="flex gap-4 flex-wrap mb-4">
                  {[pet.photo ?? []].flat().map((item, idx) => (
                    <PetAssetItem
                      key={item.id}
                      assetName={`Photo ${idx + 1}`}
                      url={item.asset?.url || ""}
                      contentType={item.asset?.content_type}
                    />
                  ))}
                </div>
                <ObjectItem
                  displayTitle={false}
                  detailsKey={`Animal ${index + 1}`}
                  value={Object.fromEntries(
                    Object.entries(pet).filter(
                      ([key]) =>
                        !["photo", "supporting_documents"].includes(key)
                    )
                  )}
                  order={[
                    "type",
                    "other_type",
                    "breed",
                    "weight",
                    "name",
                    "date_of_birth",
                    "age",
                    "service_animal"
                  ]}
                  rename={{ service_animal: "Is a service / ESA animal" }}
                />
                <Show if={Boolean(pet.supporting_documents?.length)}>
                  <div className="space-y-3">
                    <p className="text-sm text-content-300 print:text-content-800">
                      Supporting documents:
                    </p>
                    <div className="flex gap-4 flex-wrap">
                      {pet.supporting_documents?.map((item, idx) => {
                        return (
                          <PetAssetItem
                            key={item.id}
                            assetName={`Document ${idx + 1}`}
                            url={item.asset?.url || ""}
                            contentType={item.asset?.content_type}
                          />
                        );
                      })}
                    </div>
                  </div>
                </Show>
              </ContentTitle>
            ))}
          </div>
        </Section>
      )}

      {"do_you_have_vehicles" in (query.data?.report || {}) && (
        <Section sectionTitle="Vehicles" icon={<CarIcon />}>
          <div className="w-full max-w-[600px]">
            {query.data?.report?.vehicles?.length > 0 ? (
              <DynamicKeyValue
                detailsKey="Vehicle"
                value={query.data.report.vehicles}
                order={[
                  "make",
                  "model",
                  "year",
                  "color",
                  "license_plate_number",
                  "license_plate_state"
                ]}
              />
            ) : (
              <DynamicKeyValue
                detailsKey="Vehicles"
                value={query.data?.report?.do_you_have_vehicles}
              />
            )}
          </div>
        </Section>
      )}

      {"additional_questions" in (query.data?.report || {}) && (
        <Section
          sectionTitle="Additional Questions"
          icon={<AdditionalInformationIcon />}
        >
          <div className="w-full max-w-[600px]">
            {Object.keys(query.data?.report?.additional_questions).length >
            0 ? (
              <DynamicKeyValue
                order={additionalQuestionsOrder}
                detailsKey="Question"
                value={query.data.report.additional_questions}
              />
            ) : (
              <p className="text-sm font-medium text-content-300">
                No Additional Questions provided
              </p>
            )}
          </div>
        </Section>
      )}
    </div>
  );
};
